import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Trash2, Plus, Save, LogOut } from "lucide-react";

const emptyProduct = {
  category: "wheels", name: "", price: 0, description: "", images: [""], status: "available", inventory: 0, limited: false,
  size: "", bolt_pattern: "", offset: "", backspacing: "", finish: "", beadlock: false,
  sizes: [], colors: [],
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user) return <div className="bg-ink min-h-screen grid place-items-center text-zinc-500 text-mono uppercase tracking-widest">Loading…</div>;
  if (!user.is_admin) return null;

  return (
    <div data-testid="admin-dashboard" className="bg-ink text-white min-h-screen pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-mono text-xs uppercase tracking-[0.4em] text-adrenaline">Admin</div>
            <h1 className="text-display text-5xl md:text-7xl uppercase leading-none mt-3">Drop Control</h1>
            <div className="text-mono text-xs text-zinc-500 mt-3">Signed in as {user.email}</div>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} className="text-mono text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-adrenaline flex items-center gap-2"><LogOut size={14} /> Logout</button>
        </div>
        <div className="section-divider mt-10" />
        <div className="flex flex-wrap gap-1 mt-8 border-b border-white/10">
          {["products", "wheel-orders", "settings", "gallery", "waitlist", "contacts"].map((t) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)} className={`px-5 py-3 text-heading uppercase tracking-[0.2em] text-xs border-b-2 -mb-px ${tab === t ? "border-adrenaline text-white" : "border-transparent text-zinc-500 hover:text-white"}`}>{t.replace("-", " ")}</button>
          ))}
        </div>
        <div className="mt-10">
          {tab === "products" && <ProductsPanel />}
          {tab === "wheel-orders" && <WheelOrdersPanel />}
          {tab === "settings" && <SettingsPanel />}
          {tab === "gallery" && <GalleryPanel />}
          {tab === "waitlist" && <WaitlistPanel />}
          {tab === "contacts" && <ContactsPanel />}
        </div>
      </div>
    </div>
  );
}

function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      const payload = { ...data, price: parseFloat(data.price), inventory: parseInt(data.inventory) || 0, images: (data.images || []).filter(Boolean) };
      if (data.id) await api.put(`/admin/products/${data.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Product saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="text-heading uppercase tracking-widest text-zinc-300">Products ({products.length})</div>
        <button data-testid="add-product-btn" onClick={() => setEditing({ ...emptyProduct })} className="bg-adrenaline text-white px-5 py-2 text-mono text-xs uppercase tracking-[0.3em] flex items-center gap-2 hover:bg-red-600"><Plus size={14} /> New Product</button>
      </div>
      <div className="grid gap-2">
        {products.map((p) => (
          <div key={p.id} className="bg-carbon border border-white/5 p-4 flex items-center gap-4">
            <img src={p.images?.[0]} alt="" className="w-14 h-14 object-cover bg-ink" />
            <div className="flex-1">
              <div className="text-heading uppercase text-sm">{p.name}</div>
              <div className="text-mono text-xs text-zinc-500">{p.category} · ${p.price} · {p.status} · stock {p.inventory}</div>
            </div>
            <button onClick={() => setEditing({ ...p })} className="text-mono text-xs uppercase tracking-[0.3em] text-zinc-300 hover:text-white">Edit</button>
            <button onClick={() => del(p.id)} className="text-adrenaline hover:text-white"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && <ProductModal value={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ProductModal({ value, onClose, onSave }) {
  const [v, setV] = useState({ ...value, sizes: (value.sizes || []).join(","), colors: (value.colors || []).join(",") });
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const submit = () => onSave({
    ...v,
    sizes: (v.sizes || "").split(",").map((s) => s.trim()).filter(Boolean),
    colors: (v.colors || "").split(",").map((s) => s.trim()).filter(Boolean),
  });

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="max-w-3xl mx-auto my-12 bg-carbon border border-white/10 p-8">
        <div className="text-heading uppercase tracking-widest text-zinc-300 mb-6">{v.id ? "Edit" : "New"} Product</div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Category">
            <select className="bg-ink border border-white/10 px-3 py-2 w-full text-white" value={v.category} onChange={(e) => set("category", e.target.value)}>
              <option value="wheels">Wheels</option><option value="apparel">Apparel</option>
            </select>
          </Field>
          <Field label="Status">
            <select className="bg-ink border border-white/10 px-3 py-2 w-full text-white" value={v.status} onChange={(e) => set("status", e.target.value)}>
              <option value="available">Available</option><option value="coming_soon">Coming Soon</option><option value="sold_out">Sold Out</option>
            </select>
          </Field>
          <Field label="Name"><Input value={v.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Price"><Input type="number" step="0.01" value={v.price} onChange={(e) => set("price", e.target.value)} /></Field>
          <Field label="Inventory"><Input type="number" value={v.inventory} onChange={(e) => set("inventory", e.target.value)} /></Field>
          <Field label="Limited"><label className="flex items-center gap-2 text-white"><input type="checkbox" checked={!!v.limited} onChange={(e) => set("limited", e.target.checked)} /> Limited drop</label></Field>
          <Field label="Description" full><textarea rows={3} className="bg-ink border border-white/10 px-3 py-2 w-full text-white" value={v.description} onChange={(e) => set("description", e.target.value)} /></Field>
          <Field label="Image URL(s) — comma separated" full>
            <Input value={(v.images || []).join(",")} onChange={(e) => set("images", e.target.value.split(",").map((s) => s.trim()))} />
          </Field>
          {v.category === "wheels" ? (
            <>
              <Field label="Size"><Input value={v.size || ""} onChange={(e) => set("size", e.target.value)} /></Field>
              <Field label="Bolt Pattern"><Input value={v.bolt_pattern || ""} onChange={(e) => set("bolt_pattern", e.target.value)} /></Field>
              <Field label="Offset"><Input value={v.offset || ""} onChange={(e) => set("offset", e.target.value)} /></Field>
              <Field label="Backspacing"><Input value={v.backspacing || ""} onChange={(e) => set("backspacing", e.target.value)} /></Field>
              <Field label="Finish"><Input value={v.finish || ""} onChange={(e) => set("finish", e.target.value)} /></Field>
              <Field label="Beadlock"><label className="flex items-center gap-2 text-white"><input type="checkbox" checked={!!v.beadlock} onChange={(e) => set("beadlock", e.target.checked)} /> Beadlock</label></Field>
            </>
          ) : (
            <>
              <Field label="Sizes (comma)"><Input value={v.sizes} onChange={(e) => set("sizes", e.target.value)} /></Field>
              <Field label="Colors (comma)"><Input value={v.colors} onChange={(e) => set("colors", e.target.value)} /></Field>
            </>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-8">
          <button onClick={onClose} className="px-5 py-2 border border-white/20 text-mono text-xs uppercase tracking-[0.3em] hover:border-white">Cancel</button>
          <button data-testid="save-product-btn" onClick={submit} className="px-5 py-2 bg-adrenaline text-white text-mono text-xs uppercase tracking-[0.3em] flex items-center gap-2 hover:bg-red-600"><Save size={14} /> Save</button>
        </div>
      </div>
    </div>
  );
}

const Input = (p) => <input {...p} className="bg-ink border border-white/10 px-3 py-2 w-full text-white" />;
const Field = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">{label}</div>
    {children}
  </div>
);

function SettingsPanel() {
  const [s, setS] = useState({ countdown_target: "", featured_wheel_id: "", featured_apparel_id: "", drop_name: "" });
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/settings").then((r) => setS({ ...s, ...r.data, countdown_target: r.data.countdown_target ? r.data.countdown_target.slice(0, 16) : "" }));
    api.get("/products").then((r) => setProducts(r.data));
     
  }, []);
  const wheelOptions = useMemo(() => products.filter((p) => p.category === "wheels"), [products]);
  const apparelOptions = useMemo(() => products.filter((p) => p.category === "apparel"), [products]);
  const save = async () => {
    try {
      const payload = { ...s, countdown_target: s.countdown_target ? new Date(s.countdown_target).toISOString() : null };
      await api.put("/admin/settings", payload);
      toast.success("Settings saved");
    } catch (e) { toast.error("Save failed"); }
  };
  return (
    <div className="max-w-2xl space-y-6">
      <Field label="Drop Name"><Input value={s.drop_name} onChange={(e) => setS({ ...s, drop_name: e.target.value })} /></Field>
      <Field label="Countdown Target (your local time)"><Input type="datetime-local" value={s.countdown_target} onChange={(e) => setS({ ...s, countdown_target: e.target.value })} /></Field>
      <Field label="Featured Wheel">
        <select className="bg-ink border border-white/10 px-3 py-2 w-full text-white" value={s.featured_wheel_id || ""} onChange={(e) => setS({ ...s, featured_wheel_id: e.target.value })}>
          <option value="">— none —</option>
          {wheelOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      <Field label="Featured Apparel">
        <select className="bg-ink border border-white/10 px-3 py-2 w-full text-white" value={s.featured_apparel_id || ""} onChange={(e) => setS({ ...s, featured_apparel_id: e.target.value })}>
          <option value="">— none —</option>
          {apparelOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      <button data-testid="save-settings-btn" onClick={save} className="px-6 py-3 bg-adrenaline text-white text-mono text-xs uppercase tracking-[0.3em] hover:bg-red-600 flex items-center gap-2"><Save size={14} /> Save Settings</button>
    </div>
  );
}

function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", image: "", caption: "" });
  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.title || !form.image) return toast.error("Title and image required");
    await api.post("/admin/gallery", form);
    setForm({ title: "", image: "", caption: "" });
    toast.success("Added"); load();
  };
  const del = async (id) => { await api.delete(`/admin/gallery/${id}`); load(); };
  return (
    <div className="space-y-8">
      <div className="grid md:grid-cols-3 gap-3 items-end">
        <Field label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
        <Field label="Image URL"><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} /></Field>
        <Field label="Caption"><Input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} /></Field>
        <button onClick={add} className="bg-adrenaline text-white px-5 py-2 text-mono text-xs uppercase tracking-[0.3em] hover:bg-red-600 h-fit">Add Image</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {items.map((g) => (
          <div key={g.id} className="relative group">
            <img src={g.image} alt={g.title} className="w-full aspect-square object-cover bg-carbon" />
            <button onClick={() => del(g.id)} className="absolute top-2 right-2 bg-adrenaline text-white p-1.5"><Trash2 size={14} /></button>
            <div className="text-mono text-[10px] uppercase tracking-widest text-zinc-400 mt-1">{g.title}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WaitlistPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/waitlist").then((r) => setItems(r.data)); }, []);
  return (
    <div>
      <div className="text-heading uppercase tracking-widest text-zinc-300 mb-6">Waitlist Signups ({items.length})</div>
      <div className="bg-carbon border border-white/5 divide-y divide-white/5">
        {items.map((i) => (
          <div key={i.id} className="p-4 flex justify-between items-center text-sm">
            <div>
              <div className="text-white">{i.email}</div>
              {i.phone && <div className="text-mono text-xs text-zinc-500">{i.phone}</div>}
            </div>
            <div className="text-mono text-xs text-zinc-500">{new Date(i.created_at).toLocaleString()}</div>
          </div>
        ))}
        {items.length === 0 && <div className="p-6 text-mono text-xs text-zinc-500">No signups yet.</div>}
      </div>
    </div>
  );
}

function ContactsPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/contact").then((r) => setItems(r.data)); }, []);
  return (
    <div>
      <div className="text-heading uppercase tracking-widest text-zinc-300 mb-6">Contact Submissions ({items.length})</div>
      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.id} className="bg-carbon border border-white/5 p-5">
            <div className="flex justify-between items-baseline gap-4">
              <div><span className="text-white text-heading uppercase">{c.name}</span> <span className="text-mono text-xs text-zinc-500 ml-2">{c.email}</span></div>
              <div className="text-mono text-xs text-zinc-500">{new Date(c.created_at).toLocaleString()}</div>
            </div>
            {c.subject && <div className="text-mono text-xs uppercase tracking-widest text-zinc-400 mt-2">{c.subject}</div>}
            <div className="text-zinc-300 mt-3 whitespace-pre-wrap">{c.message}</div>
          </div>
        ))}
        {items.length === 0 && <div className="text-mono text-xs text-zinc-500">No submissions yet.</div>}
      </div>
    </div>
  );
}


const PRODUCTION_STATUSES = [
  "Deposit Received",
  "Fitment Review",
  "Awaiting Final Approval",
  "In Production",
  "Quality Check",
  "Ready To Ship",
  "Shipped",
];

function WheelOrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/wheel-orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const openOrder = async (order) => {
    setSelected(order); // Show modal immediately with summary
    try {
      // Lazy-load photos
      const full = await api.get(`/admin/wheel-orders/${order.id}`);
      setSelected(full.data);
    } catch (e) {
      // Modal still works with summary data
    }
  };

  const updateStatus = async (orderId, production_status) => {
    try {
      await api.put(`/admin/wheel-orders/${orderId}/status`, { production_status });
      toast.success(`Status → ${production_status}`);
      load();
      if (selected?.id === orderId) setSelected({ ...selected, production_status });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div data-testid="wheel-orders-panel">
      <div className="text-heading uppercase tracking-widest text-zinc-300 mb-6">Custom Wheel Orders ({orders.length})</div>
      <div className="grid gap-2">
        {orders.map((o) => (
          <button
            key={o.id}
            onClick={() => openOrder(o)}
            data-testid={`wheel-order-${o.id}`}
            className="text-left bg-carbon border border-white/5 hover:border-adrenaline/60 p-4 transition-colors"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-heading uppercase text-sm">{o.order_number}</div>
                <div className="text-mono text-xs text-zinc-500 mt-1">{o.customer_name} · {o.customer_email}</div>
                <div className="text-mono text-xs text-zinc-400 mt-1">{o.finish_label} · ${o.amount_charged?.toLocaleString()} {o.payment_type === "deposit" ? "deposit" : "paid in full"}</div>
              </div>
              <div className="text-right">
                <div className={`text-mono text-[10px] uppercase tracking-[0.3em] ${o.payment_status === "paid" ? "text-adrenaline" : "text-zinc-500"}`}>{o.payment_status}</div>
                <div className="text-mono text-xs text-white mt-1">{o.production_status}</div>
                <div className="text-mono text-[10px] text-zinc-600 mt-1">{new Date(o.created_at).toLocaleDateString()}</div>
              </div>
            </div>
          </button>
        ))}
        {orders.length === 0 && <div className="text-mono text-xs text-zinc-500">No wheel orders yet.</div>}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto" onClick={() => setSelected(null)}>
          <div className="max-w-4xl mx-auto my-12 bg-ink border border-white/10 p-8" onClick={(e) => e.stopPropagation()} data-testid="wheel-order-detail">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-adrenaline">Order</div>
                <div className="text-display text-3xl">{selected.order_number}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-zinc-400 hover:text-white">✕</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-6">
              <Row label="Customer" value={`${selected.customer_name} · ${selected.customer_email}${selected.customer_phone ? " · " + selected.customer_phone : ""}`} />
              <Row label="Finish" value={selected.finish_label} />
              <Row label="Charged" value={`$${selected.amount_charged?.toLocaleString()} (${selected.payment_type})`} />
              {selected.balance_due > 0 && <Row label="Balance Due" value={`$${selected.balance_due?.toLocaleString()}`} />}
              <Row label="Payment Status" value={selected.payment_status} />
              <Row label="Created" value={new Date(selected.created_at).toLocaleString()} />
            </div>

            <div className="border-t border-white/10 pt-5 mb-6">
              <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Production Status</div>
              <div className="flex flex-wrap gap-2">
                {PRODUCTION_STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(selected.id, s)}
                    data-testid={`status-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`text-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border ${selected.production_status === s ? "bg-adrenaline border-adrenaline text-black" : "border-white/15 text-zinc-300 hover:border-white"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 pt-5 mb-6">
              <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Fitment</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                {Object.entries(selected.fitment || {}).map(([k, v]) => (
                  <Row key={k} label={k.replace(/_/g, " ")} value={typeof v === "boolean" ? (v ? "Yes" : "No") : (v || "—")} />
                ))}
              </div>
            </div>

            {selected.photos && Object.keys(selected.photos).length > 0 && (
              <div className="border-t border-white/10 pt-5">
                <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Vehicle Photos</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(selected.photos).map(([k, src]) => (
                    <a key={k} href={src} target="_blank" rel="noreferrer" className="block">
                      <img src={src} alt={k} className="w-full h-48 object-cover bg-carbon" />
                      <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-2">{k.replace(/_/g, " ")}</div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</div>
      <div className="text-white">{String(value || "—")}</div>
    </div>
  );
}
